/**
 * Group Store - Layered Architecture
 *
 * Domain 모델 사용으로 변경
 */

import { create } from 'zustand'
import type { Group, GroupDetail } from '@/types/domain'
import { getGroups, getGroupsWithDetails } from '@/api/group/group.service'
import { useTankStore } from '@/store/tank/useTankStore'

// ============================================
// Legacy Type (Backward Compatibility)
// ============================================

/**
 * @deprecated Use Group from @/types/domain instead
 */
import type {
  GroupListItem,
  GroupsWithSchoolsAndTanksResponse,
} from '@/types/apiResponse/groupTypes'

// ============================================
// V2 Types
// ============================================

export type TankSchoolMapping = {
  schoolId: string
  tankId: string
}

export type TankRelationMap = Record<string, { schoolId: string; batchId: string }[]>

// ============================================
// New Store Definition
// ============================================

interface GroupState {
  // State
  groups: Group[]
  groupsWithDetails: GroupDetail[]
  releasedGroups: GroupDetail[] // 비관리 그룹 (GET /group/released)
  isLoading: boolean
  error: Error | null

  // Actions
  setGroups: (groups: Group[]) => void
  setGroupsWithDetails: (groupsWithDetails: GroupDetail[]) => void
  setReleasedGroups: (releasedGroups: GroupDetail[]) => void
  loadGroups: () => Promise<void>
  loadGroupsWithDetails: () => Promise<void>
  getGroupById: (groupId: string) => Group | undefined
  getGroupWithDetails: (groupId: string) => GroupDetail | undefined
  getGroupedTankIds: () => string[]
  reset: () => void

  // V2 Selectors
  getTankSchoolMappingsByGroupId: (groupId: string) => TankSchoolMapping[]
  getTankRelationsMap: () => TankRelationMap
}

const initialState = {
  groups: [],
  groupsWithDetails: [],
  releasedGroups: [] as GroupDetail[],
  isLoading: false,
  error: null,
}

/**
 * Zustand store for managing group data
 *
 * @example
 * ```typescript
 * const { groups, groupsWithDetails, setGroupsWithDetails } = useGroupStore()
 *
 * // Set groups from API response
 * setGroups(groupListResponse)
 *
 * // Set groups with full details
 * setGroupsWithDetails(groupsWithSchoolsAndTanksResponse)
 *
 * // Get group by ID
 * const group = useGroupStore.getState().getGroupById('group-123')
 *
 * // Get grouped tank IDs
 * const tankIds = useGroupStore.getState().getGroupedTankIds()
 * ```
 */
export const useGroupStore = create<GroupState>((set, get) => ({
  ...initialState,

  // ===== Setters =====

  setGroups: (groups) => {
    set({ groups, error: null })
  },

  setGroupsWithDetails: (groupsWithDetails) => {
    set({ groupsWithDetails, error: null })
  },

  setReleasedGroups: (releasedGroups) => {
    set({ releasedGroups, error: null })
  },

  // ===== Async Actions =====

  loadGroups: async () => {
    set({ isLoading: true, error: null })
    try {
      const groupList = await getGroups() // Service Layer 사용
      set({ groups: groupList, isLoading: false })
    } catch (error) {
      set({ error: error as Error, isLoading: false })
      throw error
    }
  },

  loadGroupsWithDetails: async () => {
    set({ isLoading: true, error: null })
    try {
      const groupDetails = await getGroupsWithDetails() // Service Layer 사용
      set({ groupsWithDetails: groupDetails, isLoading: false })
    } catch (error) {
      set({ error: error as Error, isLoading: false })
      throw error
    }
  },

  // ===== Getters =====

  getGroupById: (groupId) => {
    const { groups } = get()
    return Array.isArray(groups) ? groups.find((group) => group.id === groupId) : undefined
  },

  getGroupWithDetails: (groupId) => {
    const { groupsWithDetails } = get()
    return Array.isArray(groupsWithDetails)
      ? groupsWithDetails.find((group) => group.id === groupId)
      : undefined
  },

  getGroupedTankIds: () => {
    const { groupsWithDetails } = get()
    const tankIds = new Set<string>()

    if (Array.isArray(groupsWithDetails)) {
      groupsWithDetails.forEach((group) => {
        if (group.items && Array.isArray(group.items)) {
          group.items.forEach((item) => {
            if (item.tank && item.tank.id) {
              tankIds.add(item.tank.id)
            }
          })
        }
      })
    }

    return Array.from(tankIds)
  },

  // ===== V2 Selectors =====

  /**
   * Get tank-school mappings by group ID
   * Returns array of {schoolId, tankId} for the given group
   */
  getTankSchoolMappingsByGroupId: (groupId: string) => {
    const { groupsWithDetails } = get()

    if (!Array.isArray(groupsWithDetails)) return []

    const group = groupsWithDetails.find((g) => g.id === groupId)
    if (!group?.items) return []

    return group.items.map((item) => ({
      schoolId: item.school.id,
      tankId: item.tank.id,
    }))
  },

  /**
   * Get tank relations map
   * Returns { [tankId]: [{schoolId, batchId}] } mapping
   * Includes both grouped and ungrouped tanks with schoolId
   */
  getTankRelationsMap: () => {
    const { groupsWithDetails } = get()
    const map: TankRelationMap = {}

    // 1. Grouped tanks
    if (Array.isArray(groupsWithDetails)) {
      groupsWithDetails.forEach((group) => {
        if (!group.items) return
        group.items.forEach((item) => {
          const tankId = item.tank.id
          if (!map[tankId]) map[tankId] = []
          map[tankId].push({
            schoolId: item.school.id,
            batchId: group.id,
          })
        })
      })
    }

    // 2. Ungrouped tanks (tanks with schoolId but not in any group)
    const tanks = useTankStore.getState().tanks
    Object.values(tanks).forEach((tank) => {
      // Skip if already grouped
      if (map[tank.id]) return

      // Add ungrouped tank with schoolId
      if (tank.schoolId) {
        map[tank.id] = [{
          schoolId: tank.schoolId,
          batchId: '$ungrouped',
        }]
      }
    })

    return map
  },

  reset: () => set(initialState),
}))
